import { useEffect, useState } from 'react';
import type { AiWorkingAnimation } from '@pcp/shared';

/**
 * "AI is working" indicator — a 15–20s seamless-loop animation shown next to an
 * item's title while an AI activity runs for it. Five magical variants (chosen
 * in Settings); on completion it plays a brief "swoop" exit before unmounting.
 */
export function AiWorkingIndicator({
  active,
  variant = 'constellation',
  title = 'AI is working…',
}: {
  active: boolean;
  variant?: AiWorkingAnimation;
  title?: string;
}) {
  const [mounted, setMounted] = useState(active);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (active) { setMounted(true); setLeaving(false); return; }
    if (mounted) {
      setLeaving(true);
      const t = setTimeout(() => setMounted(false), 440); // match swoop duration
      return () => clearTimeout(t);
    }
  }, [active, mounted]);

  if (!mounted) return null;

  return (
    <span className={`aiw-wrap${leaving ? ' aiw-leave' : ' aiw-enter'}`} title={title} aria-label={title}>
      <Variant variant={variant} />
    </span>
  );
}

function Variant({ variant }: { variant: AiWorkingAnimation }) {
  switch (variant) {
    case 'morph':
      return (
        <span className="aiw-box aiw-morph">
          <span className="aiw-star aiw-star-a">✦</span>
          <span className="aiw-star aiw-star-b">❋</span>
          <i className="aiw-pop" />
        </span>
      );
    case 'aurora':
      return (
        <span className="aiw-box aiw-aurora">
          <i className="aiw-orb" />
          <i className="aiw-mote aiw-mote1" />
          <i className="aiw-mote aiw-mote2" />
          <i className="aiw-mote aiw-mote3" />
        </span>
      );
    case 'bloom':
      return (
        <span className="aiw-box aiw-bloom">
          <i className="aiw-dot aiw-dot1" />
          <i className="aiw-dot aiw-dot2" />
          <i className="aiw-dot aiw-dot3" />
          <span className="aiw-burst">✦</span>
        </span>
      );
    case 'rune':
      return (
        <span className="aiw-box aiw-rune">
          <svg viewBox="0 0 24 16" className="aiw-rune-svg" aria-hidden="true">
            <path
              className="aiw-rune-path"
              d="M5,8 C5,3.5 10,3.5 12,8 C14,12.5 19,12.5 19,8 C19,3.5 14,3.5 12,8 C10,12.5 5,12.5 5,8 Z"
            />
          </svg>
          <i className="aiw-rune-head" />
        </span>
      );
    case 'constellation':
    default:
      return (
        <span className="aiw-box aiw-constellation">
          <i className="aiw-core" />
          <span className="aiw-orbit aiw-orbit1"><i className="aiw-sat" /></span>
          <span className="aiw-orbit aiw-orbit2"><i className="aiw-sat" /></span>
          <i className="aiw-comet" />
        </span>
      );
  }
}
